import { useMutation, useQuery } from "@apollo/client";
import {
  ClipboardDocumentIcon,
  MicrophoneIcon,
  PlayIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  Alert,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import styled from "styled-components";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import Badge from "../../components/Styled/Badge";
import { Row } from "../../components/Styled/Container";
import { StyledLink } from "../../components/Styled/StyledLink";
import { User, VoicePrompt } from "../../graphql/graphql";
import { DELETE_VOICE_RECORDING } from "../../graphql/mutations-gql";
import { VOICE_RECORDINGS } from "../../graphql/queries-gql";
import { formatDateDayOnly } from "../../util/dates";

const AudioPlayer = styled.audio`
  width: 200px;
  height: 30px;
`;

const Mono = styled.span`
  font-family: ${(props) => props.theme.monoFontFamily};
  font-size: 12px;
`;

const Numeric = styled.span`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

interface VoiceRecordingWithUser extends VoicePrompt {
  user: User;
}

const VoiceRecordings = () => {
  const { data, loading, error, refetch } = useQuery(VOICE_RECORDINGS);
  const [deleteVoiceRecording] = useMutation(DELETE_VOICE_RECORDING);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const allVoiceRecordings: VoiceRecordingWithUser[] = [];
  let totalUsersChecked = 0;
  let usersWithRecordings = 0;

  if (data?.voiceRecordings) {
    totalUsersChecked = data.voiceRecordings.length;
    data.voiceRecordings.forEach((user: User) => {
      if (user.voicePrompts && user.voicePrompts.length > 0) {
        usersWithRecordings++;
        user.voicePrompts.forEach((voicePrompt: VoicePrompt) => {
          allVoiceRecordings.push({
            ...voicePrompt,
            user: user,
          });
        });
      }
    });
  }

  // Sort by newest first
  allVoiceRecordings.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());

  const handlePlay = (url: string, uploadId: string) => {
    if (playingAudio === uploadId) {
      setPlayingAudio(null);
    } else {
      setPlayingAudio(uploadId);
      const audio = new Audio(url);
      audio.play();
      audio.onended = () => setPlayingAudio(null);
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setSnackbarMessage("URL copied to clipboard!");
      setSnackbarOpen(true);
    }).catch(err => {
      console.error('Failed to copy URL:', err);
      setSnackbarMessage("Failed to copy URL");
      setSnackbarOpen(true);
    });
  };

  const handleDelete = async (userId: string, uploadId: string) => {
    if (window.confirm('Are you sure you want to delete this voice recording?')) {
      try {
        await deleteVoiceRecording({
          variables: {
            userId,
            uploadId,
          },
        });
        refetch();
      } catch (error) {
        console.error('Error deleting voice recording:', error);
      }
    }
  };

  return (
    <Page
      title="Voice recordings"
      description={
        !loading &&
        data &&
        `Checked ${totalUsersChecked} users, ${usersWithRecordings} with voice recordings.`
      }
      badge={
        !loading &&
        data && <Badge>{allVoiceRecordings.length} recordings</Badge>
      }
    >
      {error && <ErrorAlert error={error} />}

      {!loading && allVoiceRecordings.length === 0 && data && (
        <EmptyState
          Icon={MicrophoneIcon}
          title="No voice recordings found"
          description={`The voiceRecordings query returned ${totalUsersChecked} users, but none have voice recordings yet.`}
        />
      )}

      {allVoiceRecordings.length > 0 && (
        <Panel padding={0}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Text</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Upload complete</TableCell>
                  <TableCell>Audio</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allVoiceRecordings.map((recording) => (
                  <TableRow
                    key={`${recording.user._id}-${recording.uploadId}`}
                    hover
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell component="th">
                      <StyledLink to={`/user/${recording.user._id}`}>
                        <Mono>{recording.user._id}</Mono>
                      </StyledLink>
                    </TableCell>
                    <TableCell component="th">
                      {recording.user.username}
                    </TableCell>
                    <TableCell component="th">
                      {recording.text}
                    </TableCell>
                    <TableCell component="th">
                      <Numeric>{formatDateDayOnly(recording.created)}</Numeric>
                    </TableCell>
                    <TableCell component="th">
                      {recording.uploadComplete ? (
                        <Badge $tone="green">Yes</Badge>
                      ) : (
                        <Badge $tone="red">No</Badge>
                      )}
                    </TableCell>
                    <TableCell component="th">
                      {recording.uploadComplete && recording.url && (
                        <AudioPlayer
                          controls
                          src={recording.url}
                          preload="none"
                        />
                      )}
                    </TableCell>
                    <TableCell component="th" align="right">
                      <ActionsCell>
                        {recording.uploadComplete && recording.url && (
                          <>
                            <Tooltip title="Play audio">
                              <IconButton
                                size="small"
                                aria-label="Play audio"
                                onClick={() => handlePlay(recording.url, recording.uploadId)}
                              >
                                <PlayIcon width={16} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy URL">
                              <IconButton
                                size="small"
                                aria-label="Copy URL"
                                onClick={() => handleCopyUrl(recording.url)}
                              >
                                <ClipboardDocumentIcon width={16} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Delete recording">
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Delete recording"
                            onClick={() => handleDelete(recording.user._id, recording.uploadId)}
                          >
                            <TrashIcon width={16} />
                          </IconButton>
                        </Tooltip>
                      </ActionsCell>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Panel>
      )}
      {loading && (
        <Row justifyContent="center">
          <Loader />
        </Row>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Page>
  );
};

export default VoiceRecordings;
